import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Header } from './Header';
import { Footer } from './Footer';
import { Appointment, Slot, SlotConsultant } from '../../types';
import { CheckCircle, Calendar, Clock, User, Mail, Phone, MapPin, Video } from 'lucide-react';

interface AppointmentConfirmationProps {
  appointment: Appointment;
  slot: Slot;
  consultant: SlotConsultant;
}

export const AppointmentConfirmation: React.FC<AppointmentConfirmationProps> = ({
  appointment,
  slot,
  consultant,
}) => {
  const navigate = useNavigate();

  const formatDateTime = (timeString: string) => {
    const date = new Date(timeString);
    return date.toLocaleString('en-CA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Toronto'
    });
  };

  const handleGoToConsultation = () => {
    navigate('/consultation');
  };

  const handleBookAnother = () => {
    navigate('/consultation');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header
        title="Appointment Confirmed"
        subtitle="Your appointment has been successfully booked"
      />

      <div className="container mx-auto px-4 py-8 flex-1">
        <div className="max-w-2xl mx-auto">
          {/* Success Message */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-green-600 mb-2">
              Booking Successful!
            </h2>
            <p className="text-muted-foreground text-lg">
              You will receive a confirmation email shortly with all the details.
            </p>
          </div>

          {/* Appointment Details Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Appointment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Date & Time */}
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Date & Time</p>
                  <p className="text-muted-foreground">
                    {formatDateTime(slot.time)}
                  </p>
                </div>
              </div>

              {/* Duration */}
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Duration</p>
                  <p className="text-muted-foreground">
                    {slot.duration} minutes
                  </p>
                </div>
              </div>

              {/* Consultant */}
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Consultant</p>
                  <p className="text-muted-foreground">
                    {consultant.first_name}
                  </p>
                </div>
              </div>

              {/* Appointment Type */}
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Appointment Type</p>
                  <p className="text-muted-foreground">
                    {appointment.appointment_type}
                  </p>
                </div>
              </div>

              {/* Meeting Type */}
              <div className="flex items-start gap-3">
                {appointment.meeting_type === 'online' ? (
                  <Video className="h-5 w-5 text-muted-foreground mt-0.5" />
                ) : (
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                )}
                <div>
                  <p className="font-medium">Meeting Type</p>
                  <p className="text-muted-foreground">
                    {appointment.meeting_type === 'online' 
                      ? 'Online (Video Call)' 
                      : 'In-Person Meeting'
                    }
                  </p>
                </div>
              </div>

              {/* Price */}
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Amount Paid</p>
                  <p className="text-muted-foreground font-semibold text-lg">
                    {slot.pricing.price.toLocaleString("en-CA", {
                      style: "currency",
                      currency: "CAD",
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">Name</p>
                  <p className="text-muted-foreground">
                    {appointment.first_name} {appointment.last_name}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">Email</p>
                  <p className="text-muted-foreground">
                    {appointment.email}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-medium">Phone</p>
                  <p className="text-muted-foreground">
                    {appointment.contact_no}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleGoToConsultation}
              className="flex-1"
              size="lg"
            >
              Go to Consultation
            </Button>
            <Button
              onClick={handleBookAnother}
              variant="outline"
              className="flex-1"
              size="lg"
            >
              Book Another Appointment
            </Button>
          </div>

          {/* Additional Information */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">
              What's Next?
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• You will receive a confirmation email with meeting details</li>
              <li>• For online meetings, you'll receive a video call link</li>
              <li>• For in-person meetings, you'll receive the office address</li>
              <li>• Please arrive 5 minutes early for your appointment</li>
            </ul>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};
